import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonCheckbox, IonHeader, IonList, IonModal, IonTitle, IonToolbar, useIonRouter } from "@ionic/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNamedRoutes } from "../../NamedRoutes";

import
{
    User,
    UserDetails
} from '@ews/react-data';

import
{
    useCreateUser,
    useModifyUserDetails
} from "../../ReactData/user";

import { IonModalCustomEvent, OverlayEventDetail } from "@ionic/core";
import CustomerTypeAhead from "../../Customer/CustomerTypeAhead";
import { useMyProfile } from "../../Login/LoginProvider";
import { useIsMobile } from "../../Responsive";
import SettingsTextInput from "../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import modalStyle from "./NewUserModal.module.scss";
import { NewUserModalProps } from "./types";


const NewUserModal: React.FC<NewUserModalProps> = ({ trigger, onDismiss, customerId }) =>
{

    const me = useMyProfile();

    const defaultCustomerId = useMemo(() => customerId || me.customerId, [me, customerId]);

    const defaultUser: User = {
        customerId: defaultCustomerId,
        active: true
    } as User;

    const mobileView = useIsMobile();
    const { translate: t } = useL10n();
    const modal = useRef<HTMLIonModalElement>(null);

    const [user, setUser] = useState<User>(defaultUser as User);//(null);
    const { generate } = useNamedRoutes();
    const router = useIonRouter();
    const createUser = useCreateUser();
    const modifyUserDetails = useModifyUserDetails();

    async function confirm(user: User)
    {
        const { email, firstName, lastName } = user;

        if (!email || !firstName || !lastName) return;

        try {
            const createdUser = await createUser(user);
            const id = createdUser.id;

            if (!id) return;
            await modifyUserDetails(id, { firstName, lastName } as UserDetails);
            modal.current?.dismiss();

            const link = generate('user', { id });
            router.push(link);

        } catch (err) {
            console.log(err);
        }
    }

    function onWillDismiss(event: IonModalCustomEvent<OverlayEventDetail<any>>)
    {
        setUser(defaultUser);
        onDismiss && onDismiss(event);
    }

    useEffect(() =>
    {
        setUser((prev) => ({ ...prev, customerId: defaultCustomerId }));
    }, [defaultCustomerId]);

    return <IonModal
        ref={modal}
        className={modalStyle.newSystemModal}
        trigger={trigger}
        breakpoints={mobileView ? [.6] : undefined}
        onWillDismiss={onWillDismiss}>
        <IonHeader>
            <IonToolbar>
                <div className={`${modalStyle.titleCenter}`}>
                    <IonTitle>{`${t("COM_FACPMANAGEMENT_USERS_ADD_NEW")}`}</IonTitle>
                </div>
            </IonToolbar>
        </IonHeader>
        <IonList
            className="ion-padding"
            color="primary">

            <SettingsTextInput
                text={"COM_FACPMANAGEMENT_USERS_FIRSTNAME"}
                inputType={"text"}
                value={user?.firstName}
                onChange={(firstName) =>
                {
                    const _user = { ...user };

                    if (firstName !== "") {
                        _user.firstName = firstName;
                    } else {
                        delete _user.firstName;
                    }

                    setUser(_user as User);
                }} />

            <SettingsTextInput
                text={"COM_FACPMANAGEMENT_USERS_LASTNAME"}
                inputType={"text"}
                value={user?.lastName}
                onChange={(lastName) =>
                {
                    const _user = { ...user };

                    if (lastName !== "") {
                        _user.lastName = lastName;
                    } else {
                        delete _user.lastName;
                    }

                    setUser(_user as User);
                }} />

            <SettingsTextInput
                inputType="email"
                text={"COM_CONTACT_EMAIL_LABEL"}
                value={user.email}
                onChange={(email) =>
                {
                    const _user = { ...user };

                    if (email !== "") {
                        _user.email = email;
                    } else {
                        delete _user.email;
                    }

                    setUser(_user as User);
                }} />

            <CustomerTypeAhead
                id="new-user-modal"
                label="COM_FACPMANAGEMENT_BALANCE_CUSTOMER_LABEL"
                currentId={user.customerId || ""}
                onChange={(customerId) => setUser({ ...user, customerId } as User)}
            />


            <IonCheckbox
                className="ion-padding"
                labelPlacement="end"
                checked={user.active}
                onIonChange={(e) => setUser({ ...user, active: e.detail.checked } as User)}
            >
                {t("COM_BANNERS_HEADING_ACTIVE")}
            </IonCheckbox>

        </IonList>
        <div className={`${modalStyle.buttonContainer} ${modalStyle.marginBottom}`}>
            <IonButton
                slot="start"
                color={"danger"}
                onClick={() => modal.current?.dismiss()}>{t("COM_BANNERS_CANCEL")}</IonButton>
            <IonButton
                disabled={!Boolean(user.email && user.firstName && user.lastName)}
                slot="end"
                color={"success"}
                onClick={() => confirm(user)}>
                {t("COM_CONTENT_FORM_VIEW_DEFAULT_OPTION")}
            </IonButton>
        </div>
    </IonModal>;

};


export default NewUserModal;
